import { Modal, ModalBody, ModalHeader } from "flowbite-react";
import React, { useEffect, useRef } from "react";
import Hls from "hls.js";

interface VideoPreviewModalProps {
  isOpen: boolean;
  toggle: () => void;
  processedVideoUrl?: string | null;
  originalVideoUrl?: string | null;
  title?: string;
}

const VideoPreviewModal: React.FC<VideoPreviewModalProps> = ({
  isOpen,
  toggle,
  processedVideoUrl,
  originalVideoUrl,
  title,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const hlsRef = useRef<Hls | null>(null);

  // Prefer processed video over original
  const videoUrl = processedVideoUrl || originalVideoUrl;

  useEffect(() => {
    if (!isOpen) {
      // Clean up when modal closes
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
      if (videoRef.current) {
        videoRef.current.pause();
        videoRef.current.src = "";
        videoRef.current.load();
      }
      return;
    }

    if (!videoUrl) return;

    // Wait for video element to be ready
    const initVideo = () => {
      if (!videoRef.current) {
        setTimeout(initVideo, 50);
        return;
      }

      const video = videoRef.current;

      // Check if the URL is an HLS manifest
      const isHlsUrl =
        videoUrl.includes(".m3u8") || videoUrl.includes("manifest");

      if (isHlsUrl && Hls.isSupported()) {
        // Use HLS.js for HLS streams
        // Cleanup previous HLS instance
        if (hlsRef.current) {
          hlsRef.current.destroy();
        }

        const hls = new Hls();
        hls.loadSource(videoUrl);
        hls.attachMedia(video);

        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          video.play().catch((error) => {
            console.error("Auto-play failed:", error);
          });
        });

        hls.on(Hls.Events.ERROR, (_event, data) => {
          if (data.fatal) {
            switch (data.type) {
              case Hls.ErrorTypes.NETWORK_ERROR:
                console.error("Fatal network error, trying to recover");
                hls.startLoad();
                break;
              case Hls.ErrorTypes.MEDIA_ERROR:
                console.error("Fatal media error, trying to recover");
                hls.recoverMediaError();
                break;
              default:
                console.error("Fatal error, cannot recover");
                hls.destroy();
                break;
            }
          }
        });

        hlsRef.current = hls;
      } else if (
        isHlsUrl &&
        video.canPlayType("application/vnd.apple.mpegurl")
      ) {
        // Native HLS support (Safari)
        video.src = videoUrl;
        video.addEventListener("loadedmetadata", () => {
          video.play().catch((error) => {
            console.error("Auto-play failed:", error);
          });
        });
      } else {
        // Direct video file (MP4, WebM, etc.)
        video.src = videoUrl;
        video.addEventListener("loadedmetadata", () => {
          video.play().catch((error) => {
            console.error("Auto-play failed:", error);
          });
        });
      }
    };

    initVideo();
  }, [videoUrl, isOpen]);

  return (
    <Modal show={isOpen} onClose={toggle} size="lg">
      <ModalHeader className="border-b border-gray-200 p-6! dark:border-gray-700">
        <strong>{title ? `Preview: ${title}` : "Preview Video"}</strong>
      </ModalHeader>
      <ModalBody>
        {videoUrl ? (
          <>
            <video ref={videoRef} className="w-full" controls>
              <track kind="captions" />
              Your browser does not support the video tag.
            </video>
            {processedVideoUrl && originalVideoUrl && (
              <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                {processedVideoUrl
                  ? "Showing processed video"
                  : "Showing original video"}
              </p>
            )}
          </>
        ) : (
          <p className="text-center text-gray-500 dark:text-gray-400">
            No video available
          </p>
        )}
      </ModalBody>
    </Modal>
  );
};

export default VideoPreviewModal;
